import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, ArrowLeft, Save, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const US_STATES = "AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(
  " "
);

const emptyVehicle = {
  tenant_id: "",
  year: "",
  make: "",
  model: "",
  color: "",
  license_plate: "",
  state: "CA",
  images: [],
  sticker_color: "",
  permit_type: "",
};

const PERMIT_TYPES = ["Resident", "Visitor", "Reserved", "Staff", "Guest", "Other"];
const STICKER_COLORS = ["", "#0F8F4F", "#00C16E", "#09090B", "#EF4444", "#FACC15", "#A855F7", "#F97316"];

export default function VehicleForm() {
  const { activeProperty } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const [tenants, setTenants] = useState([]);
  const [form, setForm] = useState(emptyVehicle);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!activeProperty) return;
    api
      .get(`/tenants?property_id=${activeProperty.id}`)
      .then((r) => setTenants(r.data || []))
      .catch((e) => toast.error(formatApiError(e)));
    if (editId) {
      api
        .get(`/vehicles/${editId}`)
        .then((r) => setForm({ ...emptyVehicle, ...r.data }))
        .catch((e) => toast.error(formatApiError(e)));
    }
  }, [activeProperty, editId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = async (files) => {
    const arr = Array.from(files).slice(0, 4 - form.images.length);
    const b64s = await Promise.all(
      arr.map(
        (f) =>
          new Promise((resolve) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.readAsDataURL(f);
          })
      )
    );
    setForm((prev) => ({ ...prev, images: [...prev.images, ...b64s].slice(0, 4) }));
  };

  const save = async (thenAddAnother = false) => {
    if (!form.tenant_id) {
      toast.error("Select a tenant first");
      return;
    }
    setLoading(true);
    try {
      let res;
      if (editId) {
        res = await api.patch(`/vehicles/${editId}`, form);
      } else {
        res = await api.post(`/vehicles`, { ...form, property_id: activeProperty.id });
      }
      // Managers never activate manually. Activation depends on the
      // property's state:
      //   • no setup fee paid → activates automatically when setup fee
      //     is paid
      //   • setup fee paid, free-month grace still active → activates
      //     immediately (included in first-month free)
      //   • setup fee paid, free-month expired → new vehicles land in
      //     `pending_activation` and need a one-time per-car checkout.
      // The backend returns the resolved status on save, so we can
      // surface the exact next step in the toast.
      const status = res.data?.status;
      if (status === "pending_payment") {
        toast.success(
          "Vehicle saved. QR verification will activate automatically once your $99 setup payment is complete."
        );
      } else {
        toast.success(
          `Vehicle saved — ${res.data.year} ${res.data.make} ${res.data.model}. QR is active.`
        );
      }
      if (thenAddAnother) {
        setForm({ ...emptyVehicle, tenant_id: form.tenant_id, state: form.state });
      } else {
        nav("/dashboard/vehicles");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <Button variant="ghost" onClick={() => nav(-1)} className="mb-2" data-testid="vehicle-back">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back
      </Button>
      <div>
        <div className="mono-tiny text-[#71717A]">Vehicle</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
          {editId ? "Edit vehicle" : "Add a vehicle"}
        </h1>
      </div>

      {!editId && activeProperty?.setup_fee_paid_at && (
        <div
          className="bg-[#0F8F4F]/5 border border-[#0F8F4F]/20 rounded-md p-4 flex items-start gap-3"
          data-testid="vehicle-activation-hint"
        >
          <Info className="w-5 h-5 text-[#0F8F4F] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div className="text-sm text-[#0A6B3B] leading-relaxed">
            <b>Heads up:</b> your <b>$99 setup fee is paid</b>, so this new car
            activates immediately on save &mdash; no per-vehicle payment
            needed. Vehicle count is used to calculate your monthly property
            invoice.
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E4E4E7] rounded-lg p-6 md:p-8 space-y-5">
        <div className="space-y-2">
          <Label className="mono-tiny text-[#71717A]">Tenant</Label>
          <Select value={form.tenant_id} onValueChange={(v) => update("tenant_id", v)}>
            <SelectTrigger className="h-12" data-testid="vehicle-tenant">
              <SelectValue placeholder="Choose tenant" />
            </SelectTrigger>
            <SelectContent>
              {tenants.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.full_name} · Unit {t.unit_number}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Labeled label="Year">
            <Input
              value={form.year}
              onChange={(e) => update("year", e.target.value)}
              className="h-12"
              data-testid="vehicle-year"
            />
          </Labeled>
          <Labeled label="Make">
            <Input
              value={form.make}
              onChange={(e) => update("make", e.target.value)}
              className="h-12"
              data-testid="vehicle-make"
            />
          </Labeled>
          <Labeled label="Model">
            <Input
              value={form.model}
              onChange={(e) => update("model", e.target.value)}
              className="h-12"
              data-testid="vehicle-model"
            />
          </Labeled>
          <Labeled label="Color">
            <Input
              value={form.color}
              onChange={(e) => update("color", e.target.value)}
              className="h-12"
              data-testid="vehicle-color"
            />
          </Labeled>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Labeled label="License plate">
            <Input
              value={form.license_plate}
              onChange={(e) => update("license_plate", e.target.value)}
              className="h-12 font-mono uppercase"
              data-testid="vehicle-plate"
            />
          </Labeled>
          <Labeled label="State">
            <Select value={form.state} onValueChange={(v) => update("state", v)}>
              <SelectTrigger className="h-12" data-testid="vehicle-state">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Labeled>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Permit type (optional)">
            <Select
              value={form.permit_type || "none"}
              onValueChange={(v) => update("permit_type", v === "none" ? "" : v)}
            >
              <SelectTrigger className="h-12" data-testid="vehicle-permit-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {PERMIT_TYPES.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Labeled>
          <Labeled label="Sticker color override (optional)">
            <div className="flex flex-wrap gap-2 pt-1">
              {STICKER_COLORS.map((c) => (
                <button
                  key={c || "default"}
                  type="button"
                  onClick={() => update("sticker_color", c)}
                  data-testid={`vehicle-sticker-${c ? c.replace("#", "") : "default"}`}
                  className={`w-10 h-10 rounded-md border-2 flex items-center justify-center text-xs ${
                    (form.sticker_color || "") === c
                      ? "border-[#09090B] scale-110"
                      : "border-[#E4E4E7]"
                  }`}
                  style={{ backgroundColor: c || "#FFFFFF" }}
                  title={c || "Use property default"}
                >
                  {!c && <span className="text-[#71717A] text-[10px]">DEF</span>}
                </button>
              ))}
            </div>
          </Labeled>
        </div>

        <Labeled label="Photos (up to 4)">
          <div className="grid grid-cols-4 gap-3">
            {form.images.map((src, i) => (
              <div
                key={i}
                className="relative aspect-square border border-[#E4E4E7] rounded-md overflow-hidden"
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  className="absolute top-1 right-1 bg-black/60 text-white w-6 h-6 rounded-full flex items-center justify-center"
                  onClick={() =>
                    setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))
                  }
                  data-testid={`vehicle-remove-image-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {form.images.length < 4 && (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="vehicle-upload-image"
                className="aspect-square border-2 border-dashed border-[#E4E4E7] rounded-md flex flex-col items-center justify-center text-[#71717A] text-xs hover:bg-[#F4F4F5]"
              >
                <Upload className="w-5 h-5 mb-1" strokeWidth={1.5} />
                Upload
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => onFiles(e.target.files)}
          />
        </Labeled>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          onClick={() => save(true)}
          disabled={loading}
          className="h-12"
          data-testid="vehicle-save-add-another"
        >
          <Save className="w-4 h-4 mr-2" /> Save &amp; add another
        </Button>
        <Button
          onClick={() => save(false)}
          disabled={loading}
          className="h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
          data-testid="vehicle-save"
        >
          <Save className="w-4 h-4 mr-2" /> Save vehicle
        </Button>
      </div>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="mono-tiny text-[#71717A]">{label}</Label>
      {children}
    </div>
  );
}
